'use client'

import React, { FC, ReactElement } from 'react'
import { Slider } from '@/components/ui/slider'
import formatNumber from '@/utils/format-number'
import useExchangeRate from '@/utils/hooks/useExchangeRate'
import { EXPLORER_URL, API_URL, NETWORK } from '@/utils/constants'
import { Input } from '@/components/ui/input'
import Wallet from '@/utils/wallet'
import axios from 'axios'
import { useToast } from '@/components/ui/use-toast'
import useTokenOutpoints from '@/components/search-input/hooks/useTokenOutpoints'
import isRune from '@/utils/is-rune'
import { ToastAction } from '@/components/ui/toast'
import useSwr from 'swr'
import { fetcher } from '@/utils/fetcher'
import isFungible from '@/utils/is-fungible'
import isAlkanes from '@/utils/is-alkanes'
import {
	getInscriptionCollectionSlugs,
	inscriptionBelongsToCollection
} from '@/utils/inscription-collections'
import { formatSatoshis } from '@/utils/hooks/useSatoshiPrice'

import useSelect from '../../hooks/useSelect'

import Tabs from './tabs'

import Invoice from '../../components/invoice'
import { Loading } from '@/components/ui/loading'
import SelectButton from '../../components/select-button'
import Item from './base-item'
import { listingNoun } from './listing-noun'
import { onKeyboardClick } from '@/utils/a11y/keyboard-click'

/** Postage for an isolating self-send: above the 330-sat P2TR dust floor snipe protection needs, standard elsewhere. */
const ISOLATE_POSTAGE = 546

const _MARKET_FEE_RATE = 2.7
const _ARTIST_ROYALTY = 4.2

const CollectionDetailSend: FC<{
	marketActions?: React.ReactNode
	tab: string
	onChangeTab: (v: string) => void
	onNavigate: (v: string) => void
	onClose: () => void
	args: any
	floorSats: string | number
	token: any
	classification: string
	collectionFilter?: string
}> = (props): ReactElement => {
	const { onClose, args, tab, onChangeTab, token, classification, collectionFilter } = props
	const { slug } = args
	const { toast } = useToast()
	const { exchangeRate } = useExchangeRate()
	const { data: rawData, isLoading } = useTokenOutpoints(token)
	const isFrozen = slug === 'frozen'

	const data = React.useMemo(() => {
		if (!rawData || !Array.isArray(rawData)) return rawData
		if (!isFrozen || !collectionFilter || collectionFilter === 'all') return rawData
		if (collectionFilter === 'uncategorized') {
			return rawData.filter((e) => getInscriptionCollectionSlugs(e).length === 0)
		}
		return rawData.filter((e) => inscriptionBelongsToCollection(e, collectionFilter))
	}, [rawData, collectionFilter, isFrozen])

	const { data: allUtxos } = useSwr(
		`${API_URL}/wallet/${Wallet.balanceAddress()}/all-utxos`,
		fetcher
	)

	//
	// Local State
	//

	const [amount, setAmount] = React.useState(0)
	// A "Send to yourself" (isolate) open arrives with the seller's own
	// address; anything else starts blank.
	const [to, setTo] = React.useState<any>(args?.to ?? null)

	//
	// Remote Data
	//

	const ids = React.useMemo(() => {
		if (!data || !Array.isArray(data)) {
			return []
		}

		return data.map((e) => e.outpoint)
	}, [data])
	const { selected, onSelect, onSelectAll, slider, onSliderChange, allSelected } = useSelect(ids)

	//
	// Memos
	//

	const { selectedValue } = React.useMemo(() => {
		let selectedValue = 0

		// @ts-ignore
		for (let e of selected.entries()) {
			const id = e[0]
			const amount = parseFloat(data.find((a) => a.outpoint === id)?.amount)
			selectedValue += amount
		}

		return {
			selectedValue
		}
	}, [selected, data])

	//
	// Handlers
	//

	const handleChangeAmount = React.useCallback((v: any) => setAmount(v[0]), [])
	const handleChangeAmountInput = React.useCallback((evt: any) => setAmount(evt.target.value), [])
	const handleChangeTo = React.useCallback((evt: any) => setTo(evt.target.value), [])

	//
	// Renders
	//

	const renderOutpoint = React.useCallback(
		(e: any) => {
			const id = e.outpoint
			return <Item data={e} selected={selected.has(id)} onSelect={() => onSelect(id)} slug={slug} />
		},
		[onSelect, selected, slug]
	)

	const hasTransferable = React.useMemo(() => {
		const id = parseFloat(token.transferable_balance)
		return !isNaN(id) && id !== 0
	}, [token])

	const handleSubmit = React.useCallback(
		async (psbt: any) => {
			const rawtx = await Wallet.signPSBTExtractTx(psbt)
			const { data } = await axios.post(`${API_URL}/wallet/broadcast`, { rawtx: rawtx })

			if (data.error) {
				// Scriptpubkey
				if (data.error.code === -26) {
					throw new Error('Too many outputs for this amount. Choose fewer.')
				} else {
					toast({
						title: 'Failed to broadcast',
						description: data.error.message
					})
				}
			} else {
				toast({
					title: 'Success!',
					description: `You sent ${formatNumber(amount)} ${listingNoun(classification)}`,
					action: (
						<ToastAction
							altText="View Transaction"
							onClick={() => window.open(`${EXPLORER_URL}/tx/${data.result}`, '_blank')}
						>
							View Transaction
						</ToastAction>
					)
				})
			}

			onClose()
		},
		[onClose, toast, amount, classification]
	)

	const isolate = !!args?.isolate
	const handleBuildPsbt = React.useCallback(
		async (deps: any[], feeRate: number) => {
			const [dSelected, dAmount, dTo] = deps

			if (dSelected.size === 0 || !dAmount || !dTo) {
				return null
			}

			if (isRune(slug) || isAlkanes(slug)) {
				// @ts-ignore
				const divisibility = isAlkanes(slug) ? 8 : token.divisibility
				const edicts = [[token.rune_id, parseInt(dAmount), divisibility, dTo]]
				const outpoints = []

				// @ts-ignore
				for (let e of dSelected.entries()) {
					const id = e[0]
					const outpoint = data.find((e) => e.outpoint === id)
					outpoints.push([id, outpoint.sats])
				}

				const { psbt, fee } = isAlkanes(slug)
					? await Wallet.getAlkaneTransfer(edicts, outpoints, feeRate)
					: await Wallet.getRuneTransfer(edicts, outpoints, feeRate)

				return { psbt, fee }
			} else {
				if (!allUtxos?.utxos) {
					return null
				}

				try {
					const ids = []

					for (let e of dSelected.entries()) {
						const id = e[0]
						ids.push(id)
					}

					// With postage the API splits a shared UTXO by sat offset, so
					// the selected item lands alone on its own output; without it
					// the whole UTXO travels intact. Only the isolate flow asks.
					const psbt = await Wallet.getTransfers(
						ids,
						dTo,
						feeRate,
						allUtxos.utxos.map((e: any) => [e.txid, e.vout, e.value]),
						isolate ? ISOLATE_POSTAGE : undefined
					)

					const fee = Wallet.getPSBTFee(psbt)

					return { psbt, fee }
				} catch (e) {
					throw e
				}
			}
		},
		[allUtxos, data, token, slug, isolate]
	)

	// Create invoice items array
	const invoiceItems = React.useMemo(() => {
		const items = []

		// Add Total as first item (Network Fee + Service Fee)
		items.push({
			title: 'Total',
			valueFunc: ({ building, fee, psbt }: any) => {
				if (building || !psbt) return '--'
				const serviceFee = Wallet.getServiceFee(psbt)
				const totalFee = Number(fee) + serviceFee
				return `${formatSatoshis(totalFee)} ${NETWORK} / $${formatNumber(
					((totalFee / 1e8) * exchangeRate)?.toFixed(2)
				)}`
			}
		})

		// Add Service Fee
		items.push({
			title: 'Service Fee',
			valueFunc: ({ building, psbt }: any) => {
				if (building || !psbt) return '--'
				const serviceFee = Wallet.getServiceFee(psbt)
				return `${formatSatoshis(serviceFee)} ${NETWORK} / $${formatNumber(
					((serviceFee / 1e8) * exchangeRate)?.toFixed(2)
				)}`
			}
		})

		// Add Network Fee
		items.push({ title: 'Network Fee' })

		return items
	}, [exchangeRate])

	React.useEffect(() => {
		const isTokenSend = isRune(slug) || isAlkanes(slug)
		if (!isTokenSend || selectedValue < amount) {
			requestAnimationFrame(() => {
				setAmount(selectedValue)
			})
		}
	}, [selectedValue, amount, slug])

	React.useEffect(() => {
		if (selected.size == 0 && data.length) {
			if (args.inscriptionId && data.find((d) => d.outpoint === args.inscriptionId)) {
				onSelect(args.inscriptionId)
			} else {
				onSelect(data[0].outpoint)
			}
		}
	}, [selected, onSelect, data, args.inscriptionId])

	return (
		<>
			{!isFrozen && (
				<>
					<div className="flex px-2 h-7 w-full justify-between items-center">
						<p className="text-muted-foreground text-xs font-medium">My Wallet</p>
					</div>
					<div className="flex px-2 py-1.5 gap-4">
						<Tabs value={tab} onValueChange={onChangeTab} slug={slug} />
					</div>
				</>
			)}
			{(!isFungible(slug) || hasTransferable || isRune(slug) || isAlkanes(slug)) && (
				<>
					<div className="flex px-2 mt-2 flex-col w-full gap-2">
						<Input
							placeholder="Send Address"
							className="h-8"
							value={to}
							onChange={handleChangeTo}
						/>
						{(isRune(slug) || isAlkanes(slug)) && (
							<div className="flex flex-col">
								<p className="text-xs whitespace-nowrap">Send Amount: {formatNumber(amount)}</p>
								<div className="flex gap-2">
									<Slider
										defaultValue={[amount]}
										value={[amount]}
										max={selectedValue}
										min={0}
										onValueChange={handleChangeAmount}
									/>
									<Input
										placeholder="amount"
										className="w-[100px] h-8 "
										value={amount}
										onChange={handleChangeAmountInput}
									/>
								</div>
							</div>
						)}
					</div>
					<div className="flex justify-between items-center px-2 py-3 gap-2">
						<div className="flex gap-2 items-center">
							<SelectButton selected={allSelected} onSelect={onSelectAll} />
							<p className="text-xs text-muted-foreground select-none whitespace-nowrap">
								Quantity ({selected.size > 0 ? `${selected.size}/` : ''}
								{data?.length} lots)
							</p>
						</div>
						<Slider
							defaultValue={[slider]}
							value={[slider]}
							max={data?.length}
							step={1}
							onValueChange={onSliderChange}
						/>
					</div>
			{props.marketActions}
					{isLoading && (
						<div className="flex items-center justify-center min-h-[120px]">
							<Loading size={16} />
						</div>
					)}
					{data?.map(renderOutpoint)}
				</>
			)}
			{isFungible(slug) && !hasTransferable && !(isRune(slug) || isAlkanes(slug)) && (
				<p className="text-xs flex items-center justify-center h-full mt-8 gap-[2px]">
					You don't have any transferrable balance to list. Please
					<span
						className="text-violet-500 cursor-pointer"
						onClick={() => onChangeTab('split')}
						role="button"
						tabIndex={0}
						onKeyDown={onKeyboardClick(() => onChangeTab('split'))}
					>
						split
					</span>
					your balance.
				</p>
			)}
			<Invoice
				open={amount > 0 && !!to}
				onBuild={handleBuildPsbt}
				buildDeps={[selected, amount, to]}
				onSubmit={handleSubmit}
				action={`Send ${formatNumber(amount)} ${classification}`}
				items={invoiceItems}
			/>
		</>
	)
}

export default CollectionDetailSend
